import React, { useEffect, useState } from 'react';
import { deleteUser, getAllUsers } from '../services/userService';
import { EditIcon } from '../assets/icons';
import CreateUserModal from '../components/UserModals/CreateUserModal';
import EditUserModal from '../components/UserModals/EditUserModal';
import RoleBadge from '../components/common/RoleBadge';
import ActionButton from '../components/common/ActionButton';
import { maskSensitiveData } from '../helpers/stringHelpers';

const USER_ROLES = {
  1: 'Personel',
  2: 'Operasyon Şefi',
  3: 'Yönetici',
  4: 'Şöför',
  5: 'Vatandaş',
  6: 'Denetleyici'
};

const People = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchGroupTerm, setSearchGroupTerm] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Kullanıcı listesi yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleDelete = async (user) => {
      setIsLoading(true);
      setError(null);
  
      try {
        await deleteUser(user.id);
        // Update the user's status in the local state
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u.id === user.id 
              ? { ...u, isActive: !u.isActive }
              : u
          )
        );
      } catch (error) {
        setError('Kullanıcı durumu güncellenirken bir hata oluştu.');
        console.error('User status update error:', error);
      } finally {
        setIsLoading(false);
      }
  };

  const handleCreateSuccess = (newUser) => {
    setUsers(prevUsers => [...prevUsers, newUser]);
  };

  const handleEditSuccess = (updatedUser) => {
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === updatedUser.id ? updatedUser : user
      )
    );
  };

  if (isLoading) {
    return (
      <div className="people-container">
        <h1 className="page-title">Kayıtlı Kişiler</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="people-container">
        <h1 className="page-title">Kayıtlı Kişiler</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="people-container">
      <h1 className="page-title">Kayıtlı Kişiler</h1>
      <div className='add-and-search-container'>
        <div className='add-and-search-container-inner'>
          <ActionButton 
            title="Yeni Kişi Ekle"
            onClick={() => setCreateModalOpen(true)}
          />
          <div className='search-container' >
            <input
              type='text'
              placeholder='Kişi ismini yazınız.'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc' }}
            />
          </div>
          <div className='search-container' >
            <input
              type='text'
              placeholder='Grup ismini yazınız.'
              value={searchGroupTerm}
              onChange={e => setSearchGroupTerm(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc' }}
            />
          </div>
        </div>
      </div>
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Kişinin Adı ve Soyadı</th>
              <th>T.C. Kimlik Numarası</th>
              {/* <th>Mail Adresi</th> */}
              <th>Telefon Numarası</th>
              <th>Rol</th>
           
              <th>Kullanıcı Bazlı İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter(user => {
                if (searchTerm && !searchGroupTerm) {
                  const fullName = `${user.name} ${user.surname}`.toLowerCase();
                  return fullName.includes(searchTerm.toLowerCase());
                } else if (!searchTerm && searchGroupTerm) {
                  return user?.grup?.toLowerCase().includes(searchGroupTerm.toLowerCase());
                } else if (searchTerm && searchGroupTerm) {
                  const fullName = `${user.name} ${user.surname}`.toLowerCase();
                  return fullName.includes(searchTerm.toLowerCase()) && user?.grup?.toLowerCase().includes(searchGroupTerm.toLowerCase());
                }
                return user;
              })
              .map((user) => (
                <tr key={user.id}>
                  <td>{user.name} {user.surname}</td>
                  <td>{maskSensitiveData(user.tcnumber)}</td>
                  {/* <td>{user.email ?? ''}</td> */}
                  <td>{maskSensitiveData(user.phoneNumber)}</td>
                  <td><RoleBadge role={USER_ROLES[user.role]} /></td>
              
                  <td className="actions">
                    <button 
                      className="icon-button edit"
                      onClick={() => handleEdit(user)}
                      title="Düzenle"
                    >
                      Düzenle
                      <EditIcon color='black' />
                    </button>
                    <button 
                      className={`icon-button status ${user.isActive ? 'active' : 'passive'}`}
                      onClick={() => handleDelete(user)}
                      title={user.isActive ? 'Pasif Duruma Getir' : 'Aktif Duruma Getir'}
                    >
                      <div className="status-indicator">
                        {user.isActive ? 'Aktif' : 'Pasif'}
                        <input 
                          type="radio" 
                          checked={user.isActive} 
                          readOnly 
                        />
                      </div>
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <CreateUserModal 
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {selectedUser && (
        <>
          <EditUserModal
            isOpen={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
              setSelectedUser(null);
            }}
            user={selectedUser}
            onSuccess={handleEditSuccess}
          />
        </>
      )}
    </div>
  );
};

export default People;
